import React, { useState, useEffect } from "react";
import { Building2, Shield, CalendarClock, Users, Building, Briefcase, FileText, Settings, MapPin, Award, Crown, Boxes } from "lucide-react";
import OrganizationTab from "../../components/admin/tabs/organization/main/company";
import Assets from "../../components/admin/tabs/organization/main/assest";
import Inventory from "../../components/admin/tabs/organization/main/inventory";
import EmployeeGroups from "../../components/admin/tabs/organization/main/employee-group";
import Events from "../../components/admin/tabs/organization/main/event";
import Departments from "../../components/admin/tabs/organization/main/department";
import WorkLocations from "../../components/admin/tabs/organization/main/workLocation";
import Designations from "../../components/admin/tabs/organization/main/designations";
import ManageRoles from "../../components/admin/tabs/organization/security/manageroles";
import ManageUsers from "../../components/admin/tabs/organization/security/manageusers";
import { getEmployeeData } from "../../utils/localStorage";

const Organization = () => {
  const userData = getEmployeeData();
  const userRole = userData?.role;
  // Only show company-profile if companyadmin
  const showCompanyProfile = userRole === "companyadmin";

  // Compute filteredOrgSubTabs first, so we can set initial activeSubTab correctly
  const subTabs = {
    organization: [
      { id: "company-profile", icon: <Building size={16} /> },
      { id: "inventory", icon: <Boxes size={16} /> }, 
      { id: "assets", icon: <FileText size={16} /> },
      // { id: "employee-groups", icon: <Users size={16} /> },
      { id: "events", icon: <CalendarClock size={16} /> },
      { id: "departments", icon: <Briefcase size={16} /> },
      { id: "work-locations", icon: <MapPin size={16} /> },
      { id: "designations", icon: <Award size={16} /> },
    ],
    security: [
      { id: "manage-roles", icon: <Crown size={16} /> },
      { id: "manage-users", icon: <Users size={16} /> }
    ]
  };

  // Map subTab id to permission key in Organization permissions
  const orgPermissionKeyMap = {
    "company-profile": null, // only show for companyadmin
    "assets": "Assest",
    "employee-groups": "Employee Group",
    "events": "Events",
    "departments": "Department",
    "work-locations": "Work Location",
    "designations": "Designation",
    "approval-configuration": null // always show
  };

  // Filter organization sub-tabs based on permissions and companyadmin
  let filteredOrgSubTabs = subTabs.organization;
  if (userRole !== "companyadmin" && userData?.designation?.permissions?.Organization) {
    const orgPerms = userData.designation.permissions.Organization;
    filteredOrgSubTabs = subTabs.organization.filter((tab) => {
      if (tab.id === "company-profile") return false; // Only show for companyadmin
      const permKey = orgPermissionKeyMap[tab.id];
      if (!permKey) return true; // always show approval-configuration
      const permArr = orgPerms[permKey];
      return Array.isArray(permArr) && permArr.length > 0;
    });
  } else if (userRole !== "companyadmin") {
    // If not companyadmin and no permissions object, also hide company-profile
    filteredOrgSubTabs = subTabs.organization.filter((tab) => tab.id !== "company-profile");
  } else {
    // companyadmin: show all, including company-profile
    filteredOrgSubTabs = subTabs.organization;
  }

  // Set initial subtab: if companyadmin, default to company-profile, else first available
  const getInitialSubTab = () => {
    if (showCompanyProfile) return "company-profile";
    return filteredOrgSubTabs.length > 0 ? filteredOrgSubTabs[0].id : "";
  };

  const [activeTab, setActiveTab] = useState("organization");
  const [activeSubTab, setActiveSubTab] = useState(getInitialSubTab());
  const [expandedTabs, setExpandedTabs] = useState({
    organization: true,
    security: false
  });

  // If permissions/userData change, update activeSubTab if it's not in filteredOrgSubTabs
  useEffect(() => {
    if (!filteredOrgSubTabs.some(tab => tab.id === activeSubTab)) {
      setActiveSubTab(filteredOrgSubTabs.length > 0 ? filteredOrgSubTabs[0].id : "");
    }
    // eslint-disable-next-line
  }, [userData, userRole]);

  const tabs = [
    { id: "organization", icon: <Building2 size={20} /> },
    // { id: "security", icon: <Shield size={20} /> }
  ];

  const subTabComponents = {
    "company-profile": <OrganizationTab />,
    "assets": <Assets />,
    "inventory": <Inventory />,
    "employee-groups": <EmployeeGroups />,
    "events": <Events />,
    "departments": <Departments />,
    "work-locations": <WorkLocations />,
    "designations": <Designations />,
  };

  const securityComponents = {
    "manage-roles": <ManageRoles />,
    "manage-users": <ManageUsers />
  };

  const components = {
    organization: subTabComponents[activeSubTab],
    security: securityComponents[activeSubTab],
  };

  const handleTabClick = (tab) => {
    const newExpandedTabs = {
      organization: false,
      security: false
    };
    newExpandedTabs[tab] = true;
    setExpandedTabs(newExpandedTabs);
    setActiveTab(tab);

    if (tab === "organization") {
      setActiveSubTab(filteredOrgSubTabs.length > 0 ? filteredOrgSubTabs[0].id : "");
    } else if (subTabs[tab]) {
      setActiveSubTab(subTabs[tab][0].id);
    }
  };

  return (
    <div className="flex bg-white ">
      <div className="w-64 border-r border-gray-200 bg-gray-50 fixed h-screen">
        <div className="p-4">
          <div className="flex flex-col gap-2">
            {tabs.map((tab) => (
              <div key={tab.id}>
                <button
                  className={`text-left px-4 py-2 rounded-md !capitalize transition-colors duration-300 w-full flex items-center gap-2 cursor-pointer  ${
                    tab.id === activeTab
                      ? "bg-primary text-white"
                      : "hover:bg-gray-100 hover:text-primary font-bold"
                  }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.icon}
                  {tab.id}
                </button>
                {subTabs[tab.id] && (
                  <div 
                    className={`ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ease-in-out ${
                      expandedTabs[tab.id] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {(tab.id === "organization" ? filteredOrgSubTabs : subTabs[tab.id]).map((subTab) => (
                      <button
                        key={subTab.id}
                        className={`text-left px-4 py-1.5 rounded-md !capitalize text-sm transition-colors duration-300 flex items-center cursor-pointer gap-2 ${
                          subTab.id === activeSubTab
                            ? "bg-primary text-white"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => setActiveSubTab(subTab.id)}
                      >
                        {subTab.icon}
                        {subTab.id.replace(/-/g, " ")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 p-6 overflow-auto ml-64">
        <div className="bg-white rounded-lg shadow-sm">
          {components[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default Organization;
